import { Actions, createEffect, ofType } from "@ngrx/effects";
import { OrganisationService } from "src/app/_services/organisation.service";
import {
  CreateOrganisation,
  CreateOrganisationError,
  CreateOrganisationSuccess,
  DeleteOrganisation,
  DeleteOrganisationError,
  DeleteOrganisationSuccess,
  EditOrganisation,
  EditOrganisationError,
  EditOrganisationSuccess,
  GetIsOrganisationDeletable,
  GetIsOrganisationDeletableError,
  GetIsOrganisationDeletableSuccess,
  GetOrganisationDetails,
  GetOrganisationDetailsError,
  GetOrganisationDetailsSuccess,
  GetOrganisationList,
  GetOrganisationListError,
  GetOrganisationListSuccess,
  GetOrganisationPermissions,
  GetOrganisationPermissionsError,
  GetOrganisationPermissionsSuccess,
  GetOrganisationTable,
  GetOrganisationTableError,
  GetOrganisationTableLength,
  GetOrganisationTableLengthError,
  GetOrganisationTableLengthSuccess,
  GetOrganisationTableSuccess,
  IsAvailableOrgName,
  IsAvailableOrgNameError,
  IsAvailableOrgNameInvalid,
  IsAvailableOrgNameSuccess,
  RemoveItemsFromOrganisationList,
  ResetOrganisationTable,
  ResetOrganisationTableError,
  ResetOrganisationTableSuccess,
  SetOrganisationDetails,
  UpdateOrganisationTable,
  UpdateOrganisationTableError,
} from "./organisation.actions";
import { catchError, map, switchMap } from "rxjs/operators";
import { Organisation, OrganisationDetails, OrganisationPermissionsLabelDto } from "src/app/_models/Organisation";
import { of } from "rxjs";
import { Injectable } from "@angular/core";
import { OrganisationRecord } from "src/app/_models/OrganisationRecord";
import { AppConstants } from "src/app/constants/constants";
import { OrganisationPermissionService } from "src/app/_services/organisation-permission.service";
import { HttpErrorResponse } from "@angular/common/http";

@Injectable()
export class OrganisationEffects {
  constructor(
    private actions$: Actions,
    private organisationService: OrganisationService,
    private organisationPermissionService: OrganisationPermissionService
  ) {}

  public getOrganistionList$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(GetOrganisationList),
      switchMap(() =>
        this.organisationService.getOrganisations$().pipe(
          map((list: Organisation[]) => {
            return GetOrganisationListSuccess({ list: list });
          }),
          catchError((error: any) => {
            return of(GetOrganisationListError({ error: error }));
          })
        )
      )
    );
  });

  public updateOrganisationTableEffect$ = createEffect(() =>
    this.actions$.pipe(
      ofType(UpdateOrganisationTable),
      switchMap((payload) =>
        this.organisationService
          .updateOrganisationTableRecords$(payload.updateOrganisationTableDto)
          .pipe(
            map(() => {
              return GetOrganisationTable({
                pageIndex: payload.updateOrganisationTableDto.pageIndex,
                isInitialLoad: false,
                isGetLength: payload.isGetLength,
              });
            }),
            catchError((error: any) => {
              return of(UpdateOrganisationTableError({ error: error }));
            })
          )
      )
    )
  );

  public getOrganisationTableEffect$ = createEffect(() =>
    this.actions$.pipe(
      ofType(GetOrganisationTable),
      switchMap((payload) =>
        this.organisationService
          .getOrganisationTableRecord$(payload.pageIndex, payload.isInitialLoad)
          .pipe(
            map((result: OrganisationRecord[]) => {
              return GetOrganisationTableSuccess({
                result: result,
                isGetLength: payload.isGetLength,
              });
            }),
            catchError((error: any) => {
              return of(GetOrganisationTableError({ error: error }));
            })
          )
      )
    )
  );

  public getOrganisationTableLengthEffect$ = createEffect(() =>
    this.actions$.pipe(
      ofType(GetOrganisationTableLength),
      switchMap(() =>
        this.organisationService.getOrganisationTableLength$().pipe(
          map((result: number) => {
            return GetOrganisationTableLengthSuccess({ result: result });
          }),
          catchError((error: any) => {
            return of(GetOrganisationTableLengthError({ error: error }));
          })
        )
      )
    )
  );

  public resetOrganisationTable$ = createEffect(() =>
    this.actions$.pipe(
      ofType(ResetOrganisationTable),
      switchMap((payload) =>
        this.organisationService.resetOrganisationTable$().pipe(
          map(() => {
            return ResetOrganisationTableSuccess({
              isInitialLoad: payload.isInitialLoad,
            });
          }),
          catchError((error: any) => {
            return of(ResetOrganisationTableError({ error: error }));
          })
        )
      )
    )
  );

  public createOrganisationEffect$ = createEffect(() =>
    this.actions$.pipe(
      ofType(CreateOrganisation),
      switchMap((payload) =>
        this.organisationService
          .createOrganisation$(payload.organisationDto)
          .pipe(
            map((result: Organisation) => {
              return CreateOrganisationSuccess({ result: result });
            }),
            catchError((error: any) => {
              return of(CreateOrganisationError({ error: error }));
            })
          )
      )
    )
  );

  public isAvailableOrgNameEffect$ = createEffect(() =>
    this.actions$.pipe(
      ofType(IsAvailableOrgName),
      switchMap((payload) =>
        this.organisationService
          .isValidOrganisationName$(payload.orgValidationDto)
          .pipe(
            map((result: boolean | string) => {
              if (typeof result === AppConstants.BOOLEAN && result) {
                return IsAvailableOrgNameSuccess();
              } else {
                return IsAvailableOrgNameInvalid();
              }
            }),
            catchError((error: any) => {
              return of(IsAvailableOrgNameError({ error: error }));
            })
          )
      )
    )
  );

  public getOrganisationDetailsEffects$ = createEffect(() =>
    this.actions$.pipe(
      ofType(GetOrganisationDetails),
      switchMap((payload) =>
        this.organisationService
          .getOrganisationDetails$(payload.organisationId)
          .pipe(
            map((result: OrganisationDetails) => {
              return GetOrganisationDetailsSuccess({ result: result });
            }),
            catchError((error: any) => {
              return of(GetOrganisationDetailsError({ error: error }));
            })
          )
      )
    )
  );

  public setOrganisationDetailsEffects$ = createEffect(() =>
    this.actions$.pipe(
      ofType(GetOrganisationDetailsSuccess),
      map(() => {
        return SetOrganisationDetails();
      })
    )
  );

  public editOrganisationEffect$ = createEffect(() =>
    this.actions$.pipe(
      ofType(EditOrganisation),
      switchMap((payload) =>
        this.organisationService
          .editOrganisation$(payload.editOrganisationDto)
          .pipe(
            map((result: Organisation) => {
              return EditOrganisationSuccess({ result: result });
            }),
            catchError((error: any) => {
              return of(EditOrganisationError({ error: error }));
            })
          )
      )
    )
  );

  public removeItemFromOrganistionList$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(RemoveItemsFromOrganisationList),
      switchMap((payload) =>
        this.organisationService.getOrganisations$().pipe(
          map((list: Organisation[]) => {
            return GetOrganisationListSuccess({ list: list.filter((data) => !payload.orgIds.includes(data.id)) });
          }),
          catchError((error: any) => {
            return of(GetOrganisationListError({ error: error }));
          })
        )
      )
    );
  });

  public getOrganisationPermissionEffects$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(GetOrganisationPermissions),
      switchMap(() =>
        this.organisationPermissionService.getOrganisationPermissions$().pipe(
          map((list: OrganisationPermissionsLabelDto[]) => {
            return GetOrganisationPermissionsSuccess({ list: list });
          }),
          catchError((error: any) => {
            return of(GetOrganisationPermissionsError({ error: error }));
          })
        )
      )
    );
  });

  public getIsOrgDeletableEffect$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(GetIsOrganisationDeletable),
      switchMap((payload) => 
        this.organisationService.validateDelete$(payload.orgId).pipe(
          map((result: boolean) => {
            return GetIsOrganisationDeletableSuccess({result: result});
          }),
          catchError((error: HttpErrorResponse) => {
            return of(GetIsOrganisationDeletableError({error: error}));
          })
        )
      )
    )
  });

  public deleteOrganisationEffect$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(DeleteOrganisation),
      switchMap((payload) => 
        this.organisationService.deleteOrganisation$(payload.orgId).pipe(
          map((result: boolean) => {
            return DeleteOrganisationSuccess({result: result});
          }),
          catchError((error: HttpErrorResponse) => {
            return of(DeleteOrganisationError({error: error}));
          })
        )
      )
    )
  });

}
